/*
 * pn-accordion — smooth height animation for the product page's <details> accordions.
 *
 * WHY THIS EXISTS (client, 2026-08-29 — R9): a native <details> toggles instantly. The panel
 * appeared in one frame and the page shifted under the cursor, which is what "content suddenly
 * opens and the page jumps" describes. There is no CSS-only fix that works across the browsers
 * this store's shoppers actually use: ::details-content + interpolate-size is Chromium-and-new-
 * Safari only, and Firefox would still snap. So the height is animated here, and the native
 * element is left intact underneath.
 *
 * PROGRESSIVE ENHANCEMENT. With JS off or this element unregistered, the <details> keeps working
 * exactly as the browser intends — nothing here is required for the content to be reachable.
 * Under `prefers-reduced-motion: reduce` the listener bows out entirely and the native toggle runs.
 *
 * NO PAGE JUMP. Two things move a page on toggle and both are handled: the browser scrolling the
 * focused <summary> into view (killed by keeping the summary in place and never scrolling it), and
 * the document reflowing while the shopper's eye is anchored elsewhere. The scroll position is
 * sampled before the toggle and restored on the same frame if anything moved it, so the delta is 0
 * whether the shopper opens a panel from the top of the page or the bottom.
 *
 * Timing and easing come from the theme's motion tokens (--pn-dur-fast / --pn-ease-reveal), read
 * off the element, so a change to the token moves this with it.
 */
class PnAccordion extends HTMLElement {
  connectedCallback() {
    if (this.dataset.pnBound === 'true') return;
    this.dataset.pnBound = 'true';

    this.items = Array.from(this.querySelectorAll('details.pn-accordion__item'));
    this.items.forEach((details) => {
      const summary = details.querySelector('summary');
      if (!summary) return;
      summary.addEventListener('click', (event) => this.onSummaryClick(event, details));
    });
  }

  get reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  duration() {
    const raw = getComputedStyle(this).getPropertyValue('--pn-dur-fast').trim();
    const seconds = parseFloat(raw);
    if (!Number.isFinite(seconds) || seconds <= 0) return 250;
    return raw.endsWith('ms') ? seconds : seconds * 1000;
  }

  easing() {
    const raw = getComputedStyle(this).getPropertyValue('--pn-ease-reveal').trim();
    return raw || 'cubic-bezier(0.165, 0.84, 0.44, 1)';
  }

  onSummaryClick(event, details) {
    if (this.reduceMotion) return;

    event.preventDefault();

    // A second click mid-flight cancels the running animation and re-reads the live height, so
    // rapid toggling never leaves a panel stranded at a partial height.
    if (details.pnAnimation) {
      details.pnAnimation.cancel();
      details.pnAnimation = null;
    }

    const summary = details.querySelector('summary');
    const panel = details.querySelector('.pn-accordion__panel');
    if (!summary || !panel) {
      details.open = !details.open;
      return;
    }

    const opening = !details.open;
    const start = details.getBoundingClientRect().height;

    if (opening) details.open = true;

    const end = opening
      ? summary.getBoundingClientRect().height + panel.getBoundingClientRect().height
      : summary.getBoundingClientRect().height;

    details.style.overflow = 'hidden';

    const animation = details.animate(
      { height: [`${start}px`, `${end}px`] },
      { duration: this.duration(), easing: this.easing() }
    );
    details.pnAnimation = animation;

    this.holdScroll();

    animation.finished
      .then(() => {
        if (!opening) details.open = false;
      })
      .catch(() => {
        // cancel() rejects; the newer click owns the element from here.
      })
      .finally(() => {
        if (details.pnAnimation === animation) {
          details.pnAnimation = null;
          details.style.overflow = '';
          details.style.height = '';
        }
      });
  }

  // Reflow above the viewport's anchor can shift the document. Sample and restore on the next
  // frame — cheaper and more reliable than fighting the browser's scroll anchoring heuristics.
  holdScroll() {
    const y = window.scrollY;
    requestAnimationFrame(() => {
      if (window.scrollY !== y) window.scrollTo({ top: y, behavior: 'instant' });
    });
  }
}

if (!customElements.get('pn-accordion')) {
  customElements.define('pn-accordion', PnAccordion);
}
